import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/shell/PageHeader';
import ListScreen from '../../components/list/ListScreen';
import useListState from '../../components/list/useListState';
import StatusPill from '../../components/ui/StatusPill';
import { getAllBlogList } from '../../actions/blogAction';
import { formatDate } from '../../helpers/format';
import './growth.css';

/**
 * Blog posts are read from the public app API, which returns the whole set rather
 * than a page. Paging is therefore applied client-side here — when that endpoint
 * gains the shared list contract this becomes a straight makeListGetter call.
 */
function makeFetcher(dispatch) {
  return async (params) => {
    const res = await getAllBlogList(dispatch);
    let items = res?.items || [];

    if (params.search) {
      const q = params.search.toLowerCase();
      items = items.filter((p) =>
        [p.title, p.slug, p.excerpt, p.author?.name].filter(Boolean).join(' ').toLowerCase().includes(q)
      );
    }
    if (params.view === 'published') items = items.filter((p) => p.is_active);
    if (params.view === 'drafts') items = items.filter((p) => !p.is_active);

    if (params.sort) {
      const dir = params.direction === 'desc' ? -1 : 1;
      const pick = (p) =>
        ({ title: p.title, published: p.published_at, author: p.author?.name }[params.sort]);
      items = [...items].sort((a, b) => (pick(a) > pick(b) ? dir : pick(a) < pick(b) ? -dir : 0));
    }

    const total = items.length;
    const start = (params.page - 1) * params.limit;
    return { items: items.slice(start, start + params.limit), total };
  };
}

const VIEWS = [
  { key: 'all', label: 'All posts' },
  { key: 'published', label: 'Published' },
  { key: 'drafts', label: 'Drafts' },
];

export default function BlogList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fetcher = useMemo(() => makeFetcher(dispatch), [dispatch]);

  const list = useListState({
    views: VIEWS,
    fetcher,
    initialSort: { column: 'published', direction: 'desc' },
    limit: 20,
  });

  const columns = useMemo(
    () => [
      {
        key: 'title',
        header: 'Title',
        width: 300,
        sortable: true,
        render: (r) => <span style={{ fontWeight: 500 }}>{r.title}</span>,
      },
      { key: 'slug', header: 'Slug', width: 200, mono: true, muted: true },
      { key: 'excerpt', header: 'Excerpt', muted: true, render: (r) => r.excerpt || '—' },
      { key: 'author', header: 'Author', width: 150, sortable: true, render: (r) => r.author?.name || '—' },
      {
        key: 'published',
        header: 'Published',
        width: 140,
        muted: true,
        sortable: true,
        render: (r) => formatDate(r.published_at),
      },
      {
        key: 'is_active',
        header: 'Active',
        width: 110,
        render: (r) => <StatusPill tone={r.is_active ? 'live' : 'neutral'}>{r.is_active ? 'Live' : 'Draft'}</StatusPill>,
      },
    ],
    []
  );

  const openEditor = useCallback((row) => navigate(`/blog/${row.id}`), [navigate]);

  return (
    <>
      <PageHeader
        section="Growth"
        title="Blog"
        subtitle="Posts published to the Staarvani site."
        primaryAction={{ label: 'New post', icon: 'bx-plus', onClick: () => navigate('/blog/add') }}
      />

      <ListScreen
        list={list}
        columns={columns}
        rowKey={(r) => r.id}
        noun="posts"
        selectable={false}
        searchPlaceholder="Title, slug or author"
        onRowClick={openEditor}
        empty={{
          icon: 'bx-news',
          title: 'No posts',
          description: 'Write your first post to see it here.',
          actionLabel: 'New post',
          actionIcon: 'bx-plus',
          onAction: () => navigate('/blog/add'),
        }}
      />
    </>
  );
}
